import type { AuthUser } from '../types';

/**
 * Mock identity store (PTV2-1021), split out of `MockAuthProvider` so that the
 * provider file only exports a component (react-refresh hygiene). Mirrors the
 * backend `DummyIdentityFilter`: two synthetic identities chosen by the
 * `?as=platform|tenant` URL param and persisted to localStorage. There is
 * intentionally NO bridging between the planes — exactly as production keeps
 * them distinct.
 */

export type MockIdentity = 'tenant' | 'platform';

const IDENTITY_STORAGE_KEY = 'ptv2-mock-identity';

export const MOCK_TENANT_USER: AuthUser = {
  id: 'mock-user-0001',
  name: 'Morgan Tester',
  email: 'morgan.tester@example.invalid',
  status: 'ACTIVE',
  // TODO(PTV2-1021): the dummy tenant user's default tenant is hardcoded here.
  // When the dummy user is removed post-auth, per-user default-tenant storage
  // must replace this (mirrors the backend dummy-user TODO).
  tenantId: 'mock-tenant',
  // Format MUST match the `${resource}:${ACTION}` shape that `can()` checks.
  permissions: [
    'patient:READ',
    'patient:WRITE',
    'family:READ',
    // Mirrors the MSW `/me` fixture for this same user, which grants family READ+WRITE. Without
    // WRITE the `createFamilies` gate renders Not Found, making /families/new unreachable under
    // mock auth even though the UI offers it.
    'family:WRITE',
    'pedigree:READ',
    'search:READ',
    'admin:READ',
    'admin:UPDATE',
    'admin:MANAGE',
  ],
};

export const MOCK_PLATFORM_ADMIN: AuthUser = {
  id: 'mock-platform-0001',
  name: 'Pat Platform',
  email: 'pat.platform@example.invalid',
  status: 'ACTIVE',
  // No tenantId — global-plane users carry no tenant claim.
  permissions: ['platform:MANAGE', 'admin:READ'],
};

export function mockUserFor(identity: MockIdentity): AuthUser {
  return identity === 'platform' ? MOCK_PLATFORM_ADMIN : MOCK_TENANT_USER;
}

function readInitialIdentity(): MockIdentity {
  if (typeof window === 'undefined') return 'tenant';
  try {
    const param = new URLSearchParams(window.location.search).get('as');
    if (param === 'platform' || param === 'tenant') {
      window.localStorage.setItem(IDENTITY_STORAGE_KEY, param);
      return param;
    }
    const stored = window.localStorage.getItem(IDENTITY_STORAGE_KEY);
    if (stored === 'platform' || stored === 'tenant') return stored;
  } catch {
    // Ignore storage / URL access errors (private mode, SSR).
  }
  return 'tenant';
}

type IdentityListener = (identity: MockIdentity) => void;
const identityListeners = new Set<IdentityListener>();
let currentIdentity: MockIdentity = readInitialIdentity();

export function getMockIdentity(): MockIdentity {
  return currentIdentity;
}

export function setMockIdentity(identity: MockIdentity): void {
  currentIdentity = identity;
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(IDENTITY_STORAGE_KEY, identity);
    }
  } catch {
    // Ignore storage errors.
  }
  for (const listener of identityListeners) listener(identity);
}

export function subscribeMockIdentity(listener: IdentityListener): () => void {
  identityListeners.add(listener);
  return () => {
    identityListeners.delete(listener);
  };
}

/**
 * Reset the active identity back to `tenant` and clear the persisted choice.
 * Called on dev sign-out so the elevated platform identity never survives a
 * sign-out (it would otherwise persist via localStorage into the next session).
 * Does NOT notify subscribers — sign-out tears the provider down anyway, and a
 * mid-teardown notification would spuriously re-authenticate the tenant user.
 */
export function resetMockIdentity(): void {
  currentIdentity = 'tenant';
  try {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(IDENTITY_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors.
  }
}
